import { Bell } from 'lucide-react'

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen flex flex-col font-sans text-gray-900">

      {/* ── Nav ── */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 font-bold text-lg text-gray-900">
            <Bell size={20} className="text-sky-600" />
            <span>Radar Concorsi</span>
          </a>
        </div>
      </header>

      {/* ── Content ── */}
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-extrabold mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-400 mb-10">Ultimo aggiornamento: aprile 2026</p>

        <Section title="1. Titolare del trattamento">
          <p>
            Il titolare del trattamento dei dati personali è <strong>Radar Concorsi</strong>{' '}
            (radarconcorsi.it), raggiungibile all'indirizzo email{' '}
            <a href="mailto:privacy@radarconcorsi.it" className="text-sky-600 hover:underline">
              privacy@radarconcorsi.it
            </a>.
          </p>
        </Section>

        <Section title="2. Dati raccolti">
          <p>Raccogliamo le seguenti categorie di dati personali:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li><strong>Dati di account</strong>: indirizzo email e password (cifrata da Supabase Auth).</li>
            <li><strong>Dati di utilizzo</strong>: alert creati, filtri impostati, corrispondenze trovate (postings abbinati agli alert).</li>
            <li><strong>Dati tecnici</strong>: log di errore e tracce di eccezione raccolti tramite Sentry (privi di PII ove possibile); indirizzi IP gestiti dai provider di infrastruttura.</li>
          </ul>
          <p className="mt-2">Non raccogliamo dati sensibili (art. 9 GDPR), dati di pagamento (gestiti da Stripe) né dati di terze parti.</p>
        </Section>

        <Section title="3. Finalità e basi giuridiche">
          <table className="w-full text-sm mt-2 border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-2 font-semibold">Finalità</th>
                <th className="px-4 py-2 font-semibold">Base giuridica</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="px-4 py-2">Erogazione del servizio (creazione account, invio alert via email)</td>
                <td className="px-4 py-2">Esecuzione del contratto (art. 6 par. 1 lett. b GDPR)</td>
              </tr>
              <tr>
                <td className="px-4 py-2">Sicurezza e prevenzione delle frodi (blocco email usa-e-getta, CAPTCHA)</td>
                <td className="px-4 py-2">Legittimo interesse (art. 6 par. 1 lett. f GDPR)</td>
              </tr>
              <tr>
                <td className="px-4 py-2">Monitoraggio degli errori (Sentry)</td>
                <td className="px-4 py-2">Legittimo interesse (art. 6 par. 1 lett. f GDPR)</td>
              </tr>
              <tr>
                <td className="px-4 py-2">Adempimento di obblighi di legge</td>
                <td className="px-4 py-2">Obbligo legale (art. 6 par. 1 lett. c GDPR)</td>
              </tr>
            </tbody>
          </table>
        </Section>

        <Section title="4. Responsabili del trattamento (sub-processor)">
          <p>Per erogare il servizio ci avvaliamo dei seguenti fornitori, tutti con sede o adeguate garanzie per il trasferimento dati extra-UE:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li><strong>Supabase</strong> — database, autenticazione e storage (AWS, regione EU)</li>
            <li><strong>Railway</strong> — hosting del backend</li>
            <li><strong>Vercel</strong> — hosting del frontend</li>
            <li><strong>Resend</strong> — invio email transazionali</li>
            <li><strong>Stripe</strong> — gestione pagamenti (dati di carta mai in nostro possesso)</li>
            <li><strong>Sentry</strong> — monitoraggio errori (log anonimi/pseudonimizzati)</li>
            <li><strong>Cloudflare</strong> — CDN, WAF e protezione DNS</li>
          </ul>
          <p className="mt-2">
            I trasferimenti verso paesi extra-UE avvengono sulla base di Standard Contractual Clauses (SCC)
            o decisioni di adeguatezza della Commissione Europea.
          </p>
        </Section>

        <Section title="5. Conservazione dei dati">
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li><strong>Dati di account e alert</strong>: conservati per tutta la durata dell'account; eliminati entro 30 giorni dalla cancellazione dell'account.</li>
            <li><strong>Log di errore (Sentry)</strong>: conservati per 90 giorni.</li>
            <li><strong>Log di invio email (Resend)</strong>: secondo la policy di Resend (di norma 30 giorni).</li>
          </ul>
        </Section>

        <Section title="6. Diritti dell'interessato">
          <p>Hai il diritto di:</p>
          <ul className="list-disc pl-5 mt-2 space-y-1">
            <li><strong>Accesso</strong> — ottenere copia dei tuoi dati (disponibile in <em>Impostazioni → Esporta dati</em>).</li>
            <li><strong>Cancellazione</strong> — richiedere l'eliminazione di tutti i tuoi dati (disponibile in <em>Impostazioni → Elimina account</em>).</li>
            <li><strong>Portabilità</strong> — ricevere i tuoi dati in formato strutturato e leggibile da macchina (JSON).</li>
            <li><strong>Rettifica</strong> — correggere dati inesatti.</li>
            <li><strong>Opposizione e limitazione</strong> — opporti al trattamento basato su legittimo interesse o richiederne la limitazione.</li>
          </ul>
          <p className="mt-3">
            Per esercitare i tuoi diritti scrivi a{' '}
            <a href="mailto:privacy@radarconcorsi.it" className="text-sky-600 hover:underline">
              privacy@radarconcorsi.it
            </a>.
            Risponderemo entro 30 giorni. Hai anche il diritto di proporre reclamo al{' '}
            <a
              href="https://www.garanteprivacy.it"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-600 hover:underline"
            >
              Garante per la protezione dei dati personali
            </a>.
          </p>
        </Section>

        <Section title="7. Cookie e tracciamento">
          <p>
            Il sito non utilizza cookie di profilazione o di terze parti per finalità pubblicitarie.
            Vengono utilizzati esclusivamente cookie tecnici necessari al funzionamento del servizio
            (sessione di autenticazione).
          </p>
        </Section>

        <Section title="8. Sicurezza">
          <p>
            I dati sono protetti mediante comunicazioni cifrate (TLS), autenticazione con password hashata,
            controllo degli accessi tramite Row Level Security sul database, e restrizioni di rete che
            impediscono l'accesso diretto al database dall'esterno.
          </p>
        </Section>

        <Section title="9. Modifiche alla presente policy">
          <p>
            In caso di modifiche sostanziali, ti informeremo via email o tramite avviso nell'app.
            La versione aggiornata sarà sempre disponibile a questa URL.
          </p>
        </Section>
      </main>

      {/* ── Footer ── */}
      <footer className="bg-white border-t border-gray-100 py-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
          <span>© 2026 Radar Concorsi</span>
          <div className="flex gap-6">
            <a href="/" className="hover:text-gray-600 transition-colors">Home</a>
            <a href="/termini" className="hover:text-gray-600 transition-colors">Termini di servizio</a>
          </div>
        </div>
      </footer>

    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="text-xl font-bold mb-3 text-gray-800">{title}</h2>
      <div className="text-gray-600 leading-relaxed">{children}</div>
    </section>
  )
}
